import { Color } from '@persona-id/design-tokens';
import { startRegistration } from '@simplewebauthn/browser';
import { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/typescript-types';
import { useTranslation } from 'react-i18next';

import Button from 'inquiry/components/Button';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import {
  StyleValueFontWeight,
  StyleValueJustify,
  StyleValueTextTransform,
} from 'lib/inquiryStyles';
import { ApiEndpoint } from 'lib/interfaces/api';
import type { PasskeyRegistrationComponent } from 'lib/interfaces/ui-components';
import { captureException } from 'lib/reporting';
import SessionClientVerify from 'verify/lib/clients/SessionClientVerify';
import type { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import type { Inquiry } from 'verify/models/inquiry';

interface PasskeyRegistrationProps {
  actionsApi: UiComponentActionsApi;
  component: PasskeyRegistrationComponent;
  inquiry: Inquiry;
  inquiryLoading: boolean;
}

const PasskeyRegistration: React.FC<PasskeyRegistrationProps> = ({
  actionsApi,
  component,
  inquiryLoading,
}) => {
  const [t] = useTranslation();

  const {
    loading,
    error,
    callback: registerStart,
  } = useApiCallback(async () => {
    const registrationOptions = (
      await SessionClientVerify.postResourceSingletonActionNew<
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        undefined,
        undefined,
        PublicKeyCredentialCreationOptionsJSON
      >(ApiEndpoint.Persona, 'registration-initiate')
    ).meta;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const publicKeyWithAttestation = await startRegistration(registrationOptions);

    await actionsApi.onSubmit(component.name, {
      [component.name]: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'client-key': registrationOptions.user.id,
        'public-key': publicKeyWithAttestation,
        // TODO: [charles] remove this once we fetch options from new endpoints
        'expected-origin': window.location.origin,
      },
    });
  }, [actionsApi.onSubmit, component.name]);

  useApiErrorHandler({
    error,
    onNetworkError: () => actionsApi.showMessage(t('errors.network')),
    onError: (err) => {
      if (err.name !== 'NotAllowedError') {
        captureException(err, 'info');
      }
    },
  });

  return (
    <Button
      onClick={registerStart}
      disabled={loading || inquiryLoading}
      isPending={loading}
      // This must be Persona-themed, it is not customer configurable.
      styles={{
        height: { base: { base: { unit: 'px', value: 48 } } },
        width: { base: { base: { unit: '%', value: 100 } } },
        justify: { base: { base: StyleValueJustify.Start } },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        margin: { base: { base: null } },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        padding: { base: { base: null } },
        translate: {
          base: {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            base: null,
            focused: {
              x: { unit: 'px', value: 0 },
              y: { unit: 'px', value: -2 },
            },
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            active: null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            disabled: null,
          },
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'font-family': { base: { base: null } },
        'font-size': { base: { base: { unit: 'px', value: 16 } } },
        'font-weight': { base: { base: StyleValueFontWeight.Medium } },
        'letter-spacing': { base: { base: { unit: 'px', value: 0 } } },
        'line-height': { base: { base: { unit: 'px', value: 24 } } },
        'text-color': {
          base: {
            base: { unit: 'hex', value: Color.PrimaryWhite },
            focused: { unit: 'hex', value: Color.PrimaryWhite },
            active: { unit: 'hex', value: Color.PrimaryWhite },
            disabled: { unit: 'hex', value: '#9194a6' },
          },
        },
        'text-transform': {
          base: {
            base: StyleValueTextTransform.None,
          },
        },
        'background-color': {
          base: {
            // These are UBE colors, not in design system yet
            base: { unit: 'hex', value: '#3f48fd' },
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            focused: null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            active: null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            disabled: null,
          },
        },
        'border-color': {
          base: {
            base: { unit: 'hex', value: '#3f48fd' },
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            focused: null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            active: null,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            disabled: null,
          },
        },
        'border-radius': { base: { base: { unit: 'px', value: 25 } } },
        'border-width': {
          base: {
            base: {
              top: { unit: 'px', value: 2 },
              bottom: { unit: 'px', value: 2 },
              left: { unit: 'px', value: 2 },
              right: { unit: 'px', value: 2 },
            },
          },
        },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'shadow-color': { base: { base: null, focused: null, active: null, disabled: null } },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'shadow-offset': { base: { base: null, focused: null, active: null, disabled: null } },
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        'shadow-radius': { base: { base: null, focused: null, active: null, disabled: null } },
      }}
    >
      {component.attributes.label}
    </Button>
  );
};

export default PasskeyRegistration;
